import * as React from "react"
import { ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Plain `<select>` styled to match the shadcn `Select`/`Input` — for lists
 * long enough that picking an item requires scrolling (states, countries,
 * anything with 15+ options).
 *
 * Why not the usual Radix-based `Select` here: confirmed on iOS Safari
 * (WebKit) + narrow viewports that selecting an option which required
 * scrolling the listbox into view never registers — the dropdown is left
 * stuck open, covering and blocking every tap below it (this is what some
 * users reported as the page "going blank"/freezing on the public
 * application form's UF picker, which has 27 options). A short list
 * (3-6 items, nothing to scroll) doesn't hit this.
 *
 * A native `<select>` hands the picker to the OS (iOS's wheel, Android's
 * dropdown), which doesn't have this bug because it isn't a JS-simulated
 * scrollable list.
 */
const NativeSelect = React.forwardRef(({ className, children, ...props }, ref) => (
  <div className="relative">
    <select
      ref={ref}
      className={cn(
        "flex h-9 w-full appearance-none rounded-md border border-input bg-transparent px-3 py-1 pr-8 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      {...props}
    >
      {children}
    </select>
    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
  </div>
))
NativeSelect.displayName = "NativeSelect"

export { NativeSelect }
